import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Scale, Loader2, FileText, Truck, FileSignature } from 'lucide-react';
import cultivarService from '../../services/cultivar';

type Tab = 'contracts' | 'orders';

export default function LicensorDashboard() {
  const [tab, setTab] = useState<Tab>('contracts');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [contracts, setContracts] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await cultivarService.getLicensorDashboard();
      if (res.success && res.data) {
        setSummary(res.data.summary || {});
        setContracts(res.data.contracts || []);
        setOrders(res.data.orders || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredContracts = contracts.filter(c => statusFilter === 'all' || c.status === statusFilter);
  const filteredOrders = orders.filter(o => statusFilter === 'all' || o.status === statusFilter);

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      active: 'bg-emerald-100 text-emerald-700',
      pending_confirmation: 'bg-amber-100 text-amber-700',
      terminated: 'bg-red-100 text-red-700',
      suspended: 'bg-gray-200 text-gray-700',
      rejected: 'bg-red-100 text-red-700',
      superseded: 'bg-gray-100 text-gray-500',
      submitted: 'bg-blue-100 text-blue-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      fulfilled: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-gray-200 text-gray-700',
      draft: 'bg-gray-100 text-gray-600'
    };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-600'}`}>{status}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title="Licensor Dashboard" icon={<Scale className="h-6 w-6 text-white" />} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card><CardContent className="p-4">
            <p className="text-xs text-neutral-500 uppercase">Active contracts</p>
            <p className="text-2xl font-bold">{summary.active_contracts ?? 0}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-neutral-500 uppercase">Pending signatures</p>
            <p className="text-2xl font-bold">{summary.pending_contracts ?? 0}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-neutral-500 uppercase">Outstanding orders</p>
            <p className="text-2xl font-bold">{summary.outstanding_orders ?? 0}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-neutral-500 uppercase">Fulfilled orders</p>
            <p className="text-2xl font-bold">{summary.completed_orders ?? 0}</p>
          </CardContent></Card>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-1 border-b">
            <button
              className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'contracts' ? 'border-primary-600 text-primary-600' : 'border-transparent text-neutral-500'}`}
              onClick={() => setTab('contracts')}
            >
              <FileText className="h-4 w-4 inline mr-1" /> Contracts
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'orders' ? 'border-primary-600 text-primary-600' : 'border-transparent text-neutral-500'}`}
              onClick={() => setTab('orders')}
            >
              <Truck className="h-4 w-4 inline mr-1" /> Orders
            </button>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="all">All statuses</option>
              {tab === 'contracts' ? (
                <>
                  <option value="active">Active</option>
                  <option value="pending_confirmation">Pending confirmation</option>
                  <option value="suspended">Suspended</option>
                  <option value="terminated">Terminated</option>
                </>
              ) : (
                <>
                  <option value="submitted">Submitted</option>
                  <option value="accepted">Accepted</option>
                  <option value="fulfilled">Fulfilled</option>
                  <option value="cancelled">Cancelled</option>
                </>
              )}
            </select>
            <Link to="/cultivar/contracts" className="text-sm text-primary-600 flex items-center gap-1">
              <FileSignature className="h-4 w-4" /> Contracts
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>
        ) : tab === 'contracts' ? (
          <Card><CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                <tr>
                  <th className="text-left px-4 py-2">Cultivar</th>
                  <th className="text-left px-4 py-2">Licensee</th>
                  <th className="text-left px-4 py-2">Tier</th>
                  <th className="text-left px-4 py-2">Supplying nursery</th>
                  <th className="text-right px-4 py-2">Max trees</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredContracts.map(c => (
                  <tr key={c.id} className="border-t hover:bg-neutral-50">
                    <td className="px-4 py-2">{c.cultivar_trade_name || c.cultivar_name}</td>
                    <td className="px-4 py-2">{c.licensee_org_name}</td>
                    <td className="px-4 py-2">{c.license_tier}</td>
                    <td className="px-4 py-2">{c.supplying_nursery_name || '—'}</td>
                    <td className="px-4 py-2 text-right">{c.max_trees ?? '—'}</td>
                    <td className="px-4 py-2">{statusBadge(c.status)}</td>
                    <td className="px-4 py-2">
                      <Link to={`/cultivar/licences/${c.id}/agreement`} className="text-primary-600 hover:underline">View</Link>
                    </td>
                  </tr>
                ))}
                {filteredContracts.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-400">No contracts</td></tr>
                )}
              </tbody>
            </table>
          </CardContent></Card>
        ) : (
          <Card><CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                <tr>
                  <th className="text-left px-4 py-2">Cultivar</th>
                  <th className="text-left px-4 py-2">Orchard</th>
                  <th className="text-left px-4 py-2">Nursery</th>
                  <th className="text-right px-4 py-2">Trees</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Requested</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(o => (
                  <tr key={o.id} className="border-t hover:bg-neutral-50">
                    <td className="px-4 py-2">{o.cultivar_trade_name || o.cultivar_name}</td>
                    <td className="px-4 py-2">{o.orchard_org_name}</td>
                    <td className="px-4 py-2">{o.nursery_org_name}</td>
                    <td className="px-4 py-2 text-right">{o.qty_trees}</td>
                    <td className="px-4 py-2">{statusBadge(o.status)}</td>
                    <td className="px-4 py-2">{o.requested_at ? String(o.requested_at).slice(0, 10) : '—'}</td>
                  </tr>
                ))}
                {filteredOrders.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-neutral-400">No orders</td></tr>
                )}
              </tbody>
            </table>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
